// МАР.5/6/7/12/13/14/15: Внутриигровой маркетплейс карт (Этап 56).
//
// 4 таба:
//   • «КУПИТЬ»     — активные лоты с фильтрами (фракция/валюта/тип/сорт)
//   • «ПРОДАТЬ»    — мой инвентарь с фильтрами (фракция/редкость) → тап карты
//                    → форма выставления (тип/валюта/длительность/цена)
//   • «МОИ ЛОТЫ»   — что я выставил (active + history)
//   • «МОИ СТАВКИ» — аукционы где я current_bidder
//
// Ограничения по редкости: общие/редкие/эпики нельзя продать единственную
// копию (с 4-й/3-й/2-й копий разрешено); легендарки можно продавать всегда.
// Длительность аукциона: 4ч / 24ч / 48ч.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { ScreenLayout } from '../components/ScreenLayout';
import { GameCard } from '../components/GameCard';
import { ScriptsIcon } from '../components/ScriptsIcon';
import { useTelegram } from '../telegram/useTelegram';
import { useTr } from '../i18n/useTr';
import { getCardData, type CardData } from '../lib/cardData';
import { useToast } from '../components/Toast';
import { useGameStore } from '../store/useGameStore';
import {
  getMarketListings, getMyMarketListings, getMarketSellable, getMarketConfig,
  getMarketListing, createMarketListing, cancelMarketListing,
  buyMarketListing, placeMarketBid, getMarketPool, getProfile,
  type MarketListingDto, type MarketBidDto,
  type MarketSellableItem, type MarketConfig, type MarketPool
} from '../api/client';
import styles from './MarketplaceScreen.module.css';

// Helper: после успешного действия на маркете перезагружаем профиль с
// сервера → store обновляет cards_owned / scripts / usdt → DeckEditor
// и др. экраны видят актуальный инвентарь без перезагрузки.
async function refreshGameStore() {
  try {
    const data = await getProfile();
    useGameStore.getState().syncFromServer(data);
  } catch (e: any) {
    console.warn('[market] refreshGameStore failed:', e?.message);
  }
}

type Tab = 'buy' | 'sell' | 'mine' | 'bids';
type FactionFilter = 'all' | 'deus' | 'syntetics' | 'global' | 'livlife';
type RarityFilter  = 'all' | 'common' | 'rare' | 'epic' | 'legendary';
type CurrencyFilter = 'all' | 'scripts' | 'usdt';
type TypeFilter = 'all' | 'fix' | 'auction';
type Sort = 'new' | 'price_asc' | 'price_desc' | 'ending_soon';
type AuctionDuration = 4 | 24 | 48;

// Свой playerId из JWT (sub claim). Декодим payload base64.
function getMyPlayerIdFromToken(): number | null {
  try {
    const tok = localStorage.getItem('corps.token');
    if (!tok) return null;
    const payload = JSON.parse(atob(tok.split('.')[1]));
    return typeof payload.sub === 'number' ? payload.sub : null;
  } catch { return null; }
}

// ============================================================
// Утилиты форматирования
// ============================================================
function fmtPrice(amount: number | null, currency: 'scripts' | 'usdt'): string {
  if (amount == null) return '—';
  if (currency === 'usdt') return `$${(amount / 100).toFixed(2)}`;
  return amount.toLocaleString('ru-RU');
}

function fmtRemaining(expiresAt: string | null, t: (ru: string, en: string) => string): string {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return t('закрыт', 'closed');
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 0) return `${h}${t('ч', 'h')} ${m}${t('м', 'm')}`;
  return `${m}${t('м', 'm')}`;
}

function listingPrice(l: MarketListingDto): number | null {
  if (l.type === 'fix') return l.fixPrice;
  return l.currentBid ?? l.startBid;
}

const RARITY_ORDER: Record<string, number> = {
  legendary: 4, epic: 3, rare: 2, common: 1, special: 0
};

// ============================================================
// CardInfoBox — отдельный блок с описанием способности + статами.
// Текст крупный, читаемый. Используется в модалках маркета.
// ============================================================
function CardInfoBox({ card }: { card: CardData }) {
  const t = useTr();
  const a = card.ability_text;
  const hasAbility = a && (a.order || a.trigger || a.deathrattle || a.active || a.aura);
  const factionLabel: Record<string, string> = {
    deus: 'Деус', syntetics: 'Синтетикс', global: 'Глобал', livlife: 'Лив Лайф', neutral: 'Нейтрал'
  };
  const rarityLabel: Record<string, string> = {
    common: 'Обычная', rare: 'Редкая', epic: 'Эпическая', legendary: 'Легендарная', special: 'Спец.'
  };
  return (
    <div className={styles.cardInfoBox}>
      <div className={styles.cardInfoHead}>
        <strong>{card.name_ru}</strong>
        <span className={styles.cardInfoMeta}>
          {rarityLabel[card.rarity] || card.rarity} · {factionLabel[card.faction] || card.faction}
        </span>
      </div>
      {(card.atk != null || card.armor != null || card.hp != null) && (
        <div className={styles.cardInfoStats}>
          {card.atk   != null && <span><b>⚔ {card.atk}</b> {t('атк', 'atk')}</span>}
          {card.armor != null && <span><b>🛡 {card.armor}</b> {t('брн', 'arm')}</span>}
          {card.hp    != null && <span><b>❤ {card.hp}</b> hp</span>}
        </div>
      )}
      {card.keywords && card.keywords.length > 0 && (
        <div className={styles.cardInfoKeywords}>
          {card.keywords.map(kw => (
            <span key={kw} className={styles.kwTag}>{kw}</span>
          ))}
        </div>
      )}
      {hasAbility && (
        <div className={styles.cardInfoAbility}>
          {a!.order      && <div><b>{t('Приказ:', 'Order:')}</b> {a!.order}</div>}
          {a!.trigger    && <div><b>{t('Триггер:', 'Trigger:')}</b> {a!.trigger}</div>}
          {a!.aura       && <div><b>{t('Аура:', 'Aura:')}</b> {a!.aura}</div>}
          {a!.active     && <div><b>{t('Активка:', 'Active:')}</b> {a!.active}</div>}
          {a!.deathrattle && <div><b>{t('Посмертие:', 'Deathrattle:')}</b> {a!.deathrattle}</div>}
        </div>
      )}
      {card.flavor_ru && (
        <div className={styles.cardInfoFlavor}>«{card.flavor_ru}»</div>
      )}
    </div>
  );
}

// ============================================================
// Главный экран
// ============================================================
export function MarketplaceScreen() {
  const { hapticTap } = useTelegram();
  const toast = useToast(s => s.show);
  const t = useTr();
  const [tab, setTab] = useState<Tab>('buy');

  // Фильтры для tab='buy'.
  const [faction, setFaction] = useState<FactionFilter>('all');
  const [currency, setCurrency] = useState<CurrencyFilter>('all');
  const [typeF, setTypeF] = useState<TypeFilter>('all');
  const [sort, setSort] = useState<Sort>('new');

  // Фильтры для tab='sell'.
  const [sellFaction, setSellFaction] = useState<FactionFilter>('all');
  const [sellRarity,  setSellRarity]  = useState<RarityFilter>('all');

  // Данные.
  const [listings, setListings] = useState<MarketListingDto[] | null>(null);
  const [sellable, setSellable] = useState<MarketSellableItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Модалки.
  const [sellCardId, setSellCardId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  // Сезонный пул.
  const [pool, setPool] = useState<MarketPool | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'buy') {
        const res = await getMarketListings({
          faction: faction === 'all' ? undefined : faction,
          currency: currency === 'all' ? undefined : currency,
          type: typeF === 'all' ? undefined : typeF,
          sort,
          limit: 100
        });
        setListings(res.listings);
      } else if (tab === 'sell') {
        const res = await getMarketSellable();
        setSellable(res.items);
      } else if (tab === 'mine') {
        const [active, sold] = await Promise.all([
          getMyMarketListings('active'),
          getMyMarketListings('sold')
        ]);
        setListings([...active.listings, ...sold.listings]);
      } else if (tab === 'bids') {
        const res = await getMyMarketListings('bids');
        setListings(res.listings);
      }
    } catch (e: any) {
      setError(e?.message || 'load_failed');
      setListings([]);
      setSellable([]);
    } finally {
      setLoading(false);
    }
  }, [tab, faction, currency, typeF, sort]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    getMarketPool().then(setPool).catch(() => setPool(null));
  }, []);

  const handleTab = (next: Tab) => () => {
    hapticTap();
    setTab(next);
  };

  // Фильтрованный инвентарь по фракции+редкости, сортировка по редкости desc.
  const filteredSellable = useMemo(() => {
    if (!sellable) return [];
    let list = sellable;
    if (sellFaction !== 'all') list = list.filter(it => it.faction === sellFaction);
    if (sellRarity  !== 'all') list = list.filter(it => it.rarity  === sellRarity);
    return [...list].sort((a, b) => {
      const ra = RARITY_ORDER[a.rarity] ?? 0;
      const rb = RARITY_ORDER[b.rarity] ?? 0;
      if (rb !== ra) return rb - ra;
      return a.name_ru.localeCompare(b.name_ru);
    });
  }, [sellable, sellFaction, sellRarity]);

  return (
    <ScreenLayout title={t('МАРКЕТ', 'MARKET')} backTo="/home">
      <div className={styles.root}>
        {/* Сезонный пул-баннер */}
        {pool && pool.pool.scriptsAccumulated > 0 && (
          <div className={styles.poolBanner} data-tt="mk-pool">
            <div className={styles.poolRow}>
              <span>{t('Сезонный пул', 'Seasonal pool')}:</span>
              <strong>
                <ScriptsIcon size={11} /> {pool.pool.scriptsAccumulated.toLocaleString('ru-RU')}
              </strong>
            </div>
            <div className={styles.poolSub}>
              {pool.my.spentCents > 0 ? (
                <>
                  {t('Твоя доля', 'Your share')}: {pool.my.sharePercent.toFixed(2)}% ·{' '}
                  ≈ <ScriptsIcon size={9} /> {pool.my.estimatedReward.toLocaleString('ru-RU')}
                </>
              ) : (
                t('Купи карту за USDT — получи долю в конце сезона', 'Buy a card with USDT — get a share at season end')
              )}
            </div>
          </div>
        )}

        {/* 4 вкладки */}
        <div className={styles.tabs} data-tt="mk-tabs">
          <button className={tab === 'buy'  ? styles.tabActive : styles.tab} onClick={handleTab('buy')}>
            {t('КУПИТЬ', 'BUY')}
          </button>
          <button className={tab === 'sell' ? styles.tabActive : styles.tab} onClick={handleTab('sell')}>
            {t('ПРОДАТЬ', 'SELL')}
          </button>
          <button className={tab === 'mine' ? styles.tabActive : styles.tab} onClick={handleTab('mine')}>
            {t('МОИ ЛОТЫ', 'MY LOTS')}
          </button>
          <button className={tab === 'bids' ? styles.tabActive : styles.tab} onClick={handleTab('bids')}>
            {t('СТАВКИ', 'BIDS')}
          </button>
        </div>

        {/* Фильтры для «Купить» */}
        {tab === 'buy' && (
          <div className={styles.filters} data-tt="mk-filters">
            <div className={styles.filterRow}>
              <FactionPills value={faction} onChange={setFaction} />
            </div>
            <div className={styles.filterRow}>
              <Pill active={currency === 'all'}     onClick={() => setCurrency('all')}>{t('ВСЁ', 'ALL')}</Pill>
              <Pill active={currency === 'scripts'} onClick={() => setCurrency('scripts')}><ScriptsIcon size={10} /> Скр</Pill>
              <Pill active={currency === 'usdt'}    onClick={() => setCurrency('usdt')}>USDT</Pill>
              <span className={styles.sep} />
              <Pill active={typeF === 'all'}     onClick={() => setTypeF('all')}>{t('ВСЕ', 'ANY')}</Pill>
              <Pill active={typeF === 'fix'}     onClick={() => setTypeF('fix')}>{t('ФИКС', 'FIX')}</Pill>
              <Pill active={typeF === 'auction'} onClick={() => setTypeF('auction')}>{t('АУКЦИОН', 'AUCTION')}</Pill>
            </div>
            <div className={styles.filterRow}>
              <select className={styles.sortSelect} value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
                <option value="new">{t('Сначала новые', 'Newest first')}</option>
                <option value="price_asc">{t('Цена ↑', 'Price ↑')}</option>
                <option value="price_desc">{t('Цена ↓', 'Price ↓')}</option>
                <option value="ending_soon">{t('Скоро закрытие', 'Ending soon')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Фильтры для «Продать» */}
        {tab === 'sell' && (
          <div className={styles.filters} data-tt="mk-sell">
            <div className={styles.filterRow}>
              <FactionPills value={sellFaction} onChange={setSellFaction} />
            </div>
            <div className={styles.filterRow}>
              <Pill active={sellRarity === 'all'}       onClick={() => setSellRarity('all')}>{t('ВСЕ', 'ALL')}</Pill>
              <Pill active={sellRarity === 'common'}    onClick={() => setSellRarity('common')}>C</Pill>
              <Pill active={sellRarity === 'rare'}      onClick={() => setSellRarity('rare')}>R</Pill>
              <Pill active={sellRarity === 'epic'}      onClick={() => setSellRarity('epic')}>E</Pill>
              <Pill active={sellRarity === 'legendary'} onClick={() => setSellRarity('legendary')}>L</Pill>
            </div>
            <div className={styles.helpText}>
              {t(
                'Мин. остатки: C ≥3, R ≥2, E ≥1, L — можно одну. На сером — нельзя продать (мало копий).',
                'Min keep: C ≥3, R ≥2, E ≥1, L — any. Greyed = cannot sell (too few copies).'
              )}
            </div>
          </div>
        )}

        {/* === Контент === */}
        {loading && <div className={styles.placeholder}>{t('Загрузка…', 'Loading…')}</div>}
        {error && <div className={styles.errorBox}>{t('Ошибка', 'Error')}: {error}</div>}

        {/* Купить / Мои лоты / Ставки — общий грид листингов */}
        {(tab === 'buy' || tab === 'mine' || tab === 'bids') && listings && (
          <>
            {listings.length === 0 && (
              <div className={styles.placeholder}>
                {tab === 'buy'  && t('Нет лотов под фильтр.', 'No lots match the filter.')}
                {tab === 'mine' && t('Ты пока ничего не выставлял.', 'You haven\'t listed anything yet.')}
                {tab === 'bids' && t('Нет активных ставок.', 'No active bids.')}
              </div>
            )}
            {listings.length > 0 && (
              <div className={styles.grid} data-tt="mk-lot">
                {listings.map(l => (
                  <ListingTile key={l.id} listing={l} onClick={() => { hapticTap(); setDetailId(l.id); }} t={t} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Продать — инвентарь */}
        {tab === 'sell' && (
          <>
            {filteredSellable.length === 0 && !loading && (
              <div className={styles.placeholder}>
                {t('Нет карт под фильтр (или все нельзя продать).',
                   'No cards match the filter (or none can be sold).')}
              </div>
            )}
            {filteredSellable.length > 0 && (
              <div className={styles.grid}>
                {filteredSellable.map(it => {
                  const cd = getCardData(it.cardId);
                  if (!cd) return null;
                  const can = it.sellableCount > 0;
                  return (
                    <div
                      key={it.cardId}
                      className={`${styles.tile} ${!can ? styles.tileDim : ''}`}
                      onClick={() => {
                        if (!can) {
                          toast(t(
                            `${it.rarity}: оставь ≥${it.minKeep} копий. У тебя ${it.count} (в продаже ${it.listedCount}).`,
                            `${it.rarity}: keep ≥${it.minKeep} copies. You have ${it.count} (listed ${it.listedCount}).`
                          ), 'amber');
                          return;
                        }
                        hapticTap();
                        setSellCardId(it.cardId);
                      }}
                    >
                      <div className={styles.tileCard}>
                        <GameCard card={cd} variant="compact" cornerCount={`${it.sellableCount}/${it.count}`} />
                      </div>
                      <div className={styles.tileMeta}>
                        <div className={styles.subLine}>
                          {can
                            ? <span className={styles.fixTag}>{t('МОЖНО', 'OK')}</span>
                            : <span className={styles.statExp}>{t('нельзя — мало копий', 'cannot — too few')}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Модалка деталей лота */}
      {detailId !== null && (
        <ListingDetailModal
          listingId={detailId}
          onClose={() => setDetailId(null)}
          onAction={() => { setDetailId(null); reload(); refreshGameStore(); }}
        />
      )}

      {/* Модалка формы выставления (открывается из таба «Продать») */}
      {sellCardId !== null && (
        <SellFormModal
          cardId={sellCardId}
          onClose={() => setSellCardId(null)}
          onListed={() => {
            setSellCardId(null);
            reload();
            refreshGameStore();
            toast(t('Лот выставлен на маркете', 'Listed on marketplace'), 'green');
          }}
        />
      )}
    </ScreenLayout>
  );
}

// ============================================================
// Универсальные компоненты-пилюли
// ============================================================
function FactionPills({ value, onChange }: { value: FactionFilter; onChange: (v: FactionFilter) => void }) {
  const tr = useTr();
  const fs: { key: FactionFilter; label: string; color: string }[] = [
    { key: 'all',       label: tr('ВСЕ', 'ALL'),          color: 'var(--t-lo)' },
    { key: 'deus',      label: 'ДЕУС',     color: 'var(--deus)' },
    { key: 'syntetics', label: 'СИНТ',     color: 'var(--syn)' },
    { key: 'global',    label: 'ГЛОБ',     color: 'var(--glb, var(--neon-cy))' },
    { key: 'livlife',   label: 'ЛИВ',      color: 'var(--liv, var(--neon-li))' }
  ];
  return (
    <>
      {fs.map(f => (
        <button
          key={f.key}
          className={value === f.key ? styles.factPillActive : styles.factPill}
          style={{ borderColor: f.color, color: value === f.key ? '#000' : f.color, background: value === f.key ? f.color : 'transparent' }}
          onClick={() => onChange(f.key)}
        >
          {f.label}
        </button>
      ))}
    </>
  );
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button className={active ? styles.pillActive : styles.pill} onClick={onClick}>{children}</button>
  );
}

// ============================================================
// ListingTile — одна плитка в гриде
// ============================================================
function ListingTile({ listing: l, onClick, t }: {
  listing: MarketListingDto;
  onClick: () => void;
  t: (ru: string, en: string) => string;
}) {
  const card: CardData | null = getCardData(l.cardId);
  if (!card) return null;
  const price = listingPrice(l);
  const remaining = l.type === 'auction' ? fmtRemaining(l.expiresAt, t) : '';
  const closed = l.status !== 'active';

  return (
    <div className={`${styles.tile} ${closed ? styles.tileDim : ''}`} onClick={onClick}>
      <div className={styles.tileCard}>
        <GameCard card={card} variant="compact" />
      </div>
      <div className={styles.tileMeta}>
        <div className={styles.priceLine}>
          <strong>{fmtPrice(price, l.currency)}</strong>
          <span className={styles.curBadge}>
            {l.currency === 'usdt' ? 'USDT' : 'Скр'}
          </span>
        </div>
        <div className={styles.subLine}>
          {l.type === 'fix' ? (
            <span className={styles.fixTag}>FIX</span>
          ) : (
            <>
              <span className={styles.aucTag}>АУК</span>
              {remaining && <span className={styles.timer}>{remaining}</span>}
            </>
          )}
          {l.status === 'sold' && <span className={styles.statSold}>{t('продано', 'sold')}</span>}
          {l.status === 'cancelled' && <span className={styles.statCancel}>{t('отменён', 'cancelled')}</span>}
          {l.status === 'expired' && <span className={styles.statExp}>{t('истёк', 'expired')}</span>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ListingDetailModal — детали + действия
// ============================================================
function ListingDetailModal({ listingId, onClose, onAction }: {
  listingId: number;
  onClose: () => void;
  onAction: () => void;
}) {
  const t = useTr();
  const { hapticTap } = useTelegram();
  const toast = useToast(s => s.show);
  const myPlayerId = getMyPlayerIdFromToken();
  const [listing, setListing] = useState<MarketListingDto | null>(null);
  const [bids, setBids] = useState<MarketBidDto[]>([]);
  const [bidAmount, setBidAmount] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMarketListing(listingId)
      .then(r => { setListing(r.listing); setBids(r.bids); })
      .catch(() => onClose());
  }, [listingId, onClose]);

  const card = listing ? getCardData(listing.cardId) : null;
  const isMine = listing && listing.sellerId === myPlayerId;
  const isMyBid = listing && listing.currentBidderId === myPlayerId;

  const onBuy = async () => {
    if (!listing) return;
    if (!confirm(t(`Купить за ${fmtPrice(listing.fixPrice!, listing.currency)}?`, `Buy for ${fmtPrice(listing.fixPrice!, listing.currency)}?`))) return;
    hapticTap();
    setBusy(true);
    try {
      await buyMarketListing(listing.id);
      toast(t('Карта куплена! Уже в твоей Колоде.', 'Card purchased! Already in your Deck.'), 'green');
      onAction();
    } catch (e: any) {
      toast(t('Ошибка', 'Error') + ': ' + (e?.payload?.error || e?.message || 'unknown'), 'red');
    } finally { setBusy(false); }
  };

  const onBid = async () => {
    if (!listing) return;
    const amt = listing.currency === 'usdt' ? Math.round(parseFloat(bidAmount) * 100) : parseInt(bidAmount, 10);
    if (!amt || amt <= 0) { toast(t('Введи сумму', 'Enter amount'), 'amber'); return; }
    hapticTap();
    setBusy(true);
    try {
      await placeMarketBid(listing.id, amt);
      toast(t('Ставка сделана', 'Bid placed'), 'green');
      onAction();
    } catch (e: any) {
      const err = e?.payload?.error;
      if (err === 'bid_too_low') {
        const min = e.payload.min;
        toast(t(`Минимальная ставка: ${fmtPrice(min, listing.currency)}`, `Min bid: ${fmtPrice(min, listing.currency)}`), 'amber');
      } else {
        toast(t('Ошибка', 'Error') + ': ' + (err || e?.message || 'unknown'), 'red');
      }
    } finally { setBusy(false); }
  };

  const onCancel = async () => {
    if (!listing) return;
    if (!confirm(t('Отменить лот?', 'Cancel listing?'))) return;
    hapticTap();
    setBusy(true);
    try {
      await cancelMarketListing(listing.id);
      toast(t('Лот отменён', 'Cancelled'), 'cyan');
      onAction();
    } catch (e: any) {
      toast(t('Ошибка', 'Error') + ': ' + (e?.payload?.error || e?.message || 'unknown'), 'red');
    } finally { setBusy(false); }
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalBody} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}>×</button>
        {!listing && <div className={styles.placeholder}>{t('Загрузка…', 'Loading…')}</div>}
        {listing && card && (
          <>
            <div className={styles.modalCard}>
              <GameCard card={card} variant="compact" />
            </div>
            <CardInfoBox card={card} />
            <div className={styles.modalInfo}>
              <div className={styles.modalRow}>
                <span>{t('Цена', 'Price')}:</span>
                <strong>{fmtPrice(listingPrice(listing), listing.currency)} {listing.currency === 'usdt' ? 'USDT' : 'Скр'}</strong>
              </div>
              <div className={styles.modalRow}>
                <span>{t('Тип', 'Type')}:</span>
                <span>{listing.type === 'fix' ? t('Фикс-цена', 'Fix price') : t('Аукцион', 'Auction')}</span>
              </div>
              {listing.type === 'auction' && (
                <div className={styles.modalRow}>
                  <span>{t('До закрытия', 'Time left')}:</span>
                  <span>{fmtRemaining(listing.expiresAt, t) || '—'}</span>
                </div>
              )}
              <div className={styles.modalRow}>
                <span>{t('Статус', 'Status')}:</span>
                <span>{listing.status}</span>
              </div>
            </div>

            {/* Действия */}
            {listing.status === 'active' && !isMine && listing.type === 'fix' && (
              <button className={styles.actionPrimary} onClick={onBuy} disabled={busy}>
                {t('КУПИТЬ', 'BUY')}
              </button>
            )}
            {listing.status === 'active' && !isMine && listing.type === 'auction' && (
              <>
                {isMyBid && <div className={styles.myBidNote}>{t('Твоя ставка лидирует', 'Your bid is leading')}</div>}
                <div className={styles.bidRow}>
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder={t('Сумма ставки', 'Bid amount')}
                    className={styles.bidInput}
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                  />
                  <button className={styles.actionPrimary} onClick={onBid} disabled={busy}>
                    {t('СТАВКА', 'BID')}
                  </button>
                </div>
              </>
            )}
            {listing.status === 'active' && isMine && (
              <button className={styles.actionDanger} onClick={onCancel} disabled={busy}>
                {t('ОТМЕНИТЬ', 'CANCEL')}
              </button>
            )}

            {/* История ставок: кто · когда · сколько */}
            {listing.type === 'auction' && (
              <div className={styles.bidHist}>
                <div className={styles.bidHistTitle}>
                  {t('История ставок', 'Bid history')}
                  {bids.length > 0 && <span className={styles.bidCount}> · {bids.length}</span>}
                </div>
                {bids.length === 0 && (
                  <div className={styles.bidEmpty}>{t('Ставок ещё нет — будь первым', 'No bids yet — be the first')}</div>
                )}
                {bids.map(b => {
                  const isMe = b.bidderId === myPlayerId;
                  const statusLabel: Record<string, string> = {
                    placed:   t('лидирует', 'leading'),
                    outbid:   t('перебита', 'outbid'),
                    won:      t('победила', 'won'),
                    refunded: t('возврат', 'refunded')
                  };
                  return (
                    <div key={b.id} className={`${styles.bidHistRow} ${isMe ? styles.bidMine : ''}`}>
                      <div className={styles.bidWho}>
                        <span className={styles.bidNick}>{isMe ? t('Ты', 'You') : b.bidderNick}</span>
                        <span className={styles.bidTime}>
                          {new Date(b.createdAt).toLocaleString('ru-RU', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className={styles.bidRight}>
                        <span className={styles.bidAmt}>{fmtPrice(b.amount, listing.currency)}</span>
                        <span className={`${styles.bidStatus} ${b.status === 'placed' ? styles.bidStatusLead : ''}`}>
                          {statusLabel[b.status] || b.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SellFormModal — форма выставления (карта уже выбрана)
// ============================================================
function SellFormModal({ cardId, onClose, onListed }: {
  cardId: string;
  onClose: () => void;
  onListed: () => void;
}) {
  const t = useTr();
  const { hapticTap } = useTelegram();
  const toast = useToast(s => s.show);
  const [config, setConfig] = useState<MarketConfig | null>(null);
  const [type, setType] = useState<'fix' | 'auction'>('fix');
  const [currency, setCurrency] = useState<'scripts' | 'usdt'>('scripts');
  const [duration, setDuration] = useState<AuctionDuration>(24);
  const [price, setPrice] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMarketConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  const card = getCardData(cardId);
  const minPrice = currency === 'usdt' ? (config?.minPriceUsdtCents || 10) : (config?.minPriceScripts || 10);
  const commissionPct = ((config?.commissionRate || 0.1) * 100).toFixed(0);
  const durations: AuctionDuration[] = (config?.auctionDurations as AuctionDuration[]) || [4, 24, 48];

  const onSubmit = async () => {
    const p = currency === 'usdt' ? Math.round(parseFloat(price) * 100) : parseInt(price, 10);
    if (!p || p < minPrice) {
      toast(t(`Минимальная цена: ${currency === 'usdt' ? '$' + (minPrice / 100).toFixed(2) : minPrice}`,
              `Min price: ${currency === 'usdt' ? '$' + (minPrice / 100).toFixed(2) : minPrice}`), 'amber');
      return;
    }
    hapticTap();
    setBusy(true);
    try {
      const data: any = { card_id: cardId, type, currency };
      if (type === 'fix') data.fix_price = p;
      else { data.start_bid = p; data.duration_hours = duration; }
      await createMarketListing(data);
      onListed();
    } catch (e: any) {
      const err = e?.payload?.error;
      if (err === 'min_keep_violation') {
        const p = e.payload;
        toast(t(
          `Нельзя продать: для редкости ${p.rarity} оставь ≥${p.minKeep} копий.`,
          `Cannot sell: for ${p.rarity} keep ≥${p.minKeep} copies.`
        ), 'amber');
      } else {
        toast(t('Ошибка', 'Error') + ': ' + (err || e?.message || 'unknown'), 'red');
      }
    } finally { setBusy(false); }
  };

  if (!card) return null;

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalBody} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}>×</button>
        <h3 className={styles.modalTitle}>{t('ВЫСТАВИТЬ КАРТУ', 'LIST A CARD')}</h3>

        <div className={styles.modalCard}>
          <GameCard card={card} variant="compact" />
        </div>
        <CardInfoBox card={card} />

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{t('Тип лота', 'Listing type')}</label>
          <div className={styles.btnRow}>
            <button className={type === 'fix' ? styles.segActive : styles.seg} onClick={() => setType('fix')}>
              {t('Фикс-цена', 'Fix price')}
            </button>
            <button className={type === 'auction' ? styles.segActive : styles.seg} onClick={() => setType('auction')}>
              {t('Аукцион', 'Auction')}
            </button>
          </div>
        </div>

        {type === 'auction' && (
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('Длительность', 'Duration')}</label>
            <div className={styles.btnRow}>
              {durations.map(d => (
                <button
                  key={d}
                  className={duration === d ? styles.segActive : styles.seg}
                  onClick={() => setDuration(d)}
                >
                  {d} {t('ч', 'h')}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{t('Валюта', 'Currency')}</label>
          <div className={styles.btnRow}>
            <button className={currency === 'scripts' ? styles.segActive : styles.seg} onClick={() => setCurrency('scripts')}>
              <ScriptsIcon size={11} /> Скрипты
            </button>
            <button className={currency === 'usdt' ? styles.segActive : styles.seg} onClick={() => setCurrency('usdt')}>
              USDT
            </button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>
            {type === 'fix' ? t('Цена', 'Price') : t('Стартовая ставка', 'Start bid')}
            {' '}({currency === 'usdt' ? 'USDT' : 'Скр'})
          </label>
          <input
            type="number"
            inputMode="decimal"
            step={currency === 'usdt' ? '0.01' : '1'}
            placeholder={currency === 'usdt' ? '0.50' : '100'}
            className={styles.bidInput}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <div className={styles.helpText}>
            {t(`Мин: ${currency === 'usdt' ? '$' + (minPrice / 100).toFixed(2) : minPrice + ' Скр'}`,
               `Min: ${currency === 'usdt' ? '$' + (minPrice / 100).toFixed(2) : minPrice + ' Scr'}`)}
            {' · '}
            {t(`Комиссия ${commissionPct}%`, `${commissionPct}% commission`)}
          </div>
        </div>

        <button className={styles.actionPrimary} onClick={onSubmit} disabled={busy}>
          {t('ВЫСТАВИТЬ', 'LIST IT')}
        </button>
      </div>
    </div>
  );
}
